import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { batchAPI } from '../../utils/api';
import { DashboardLayout } from '../../components/layout';
import { Spinner } from '../../components/ui';
import toast from 'react-hot-toast';

const statusConfig = {
  completed: { label: 'Completed', color: '#28A745', bg: '#e8f5e9', icon: '✅' },
  unlocked:  { label: 'Available', color: '#1A3C8F', bg: '#e8edf9', icon: '🔓' },
  locked:    { label: 'Locked',    color: '#6c757d', bg: '#f1f3f5', icon: '🔒' },
};

function BatchCard({ batch, onStart }) {
  const nav  = useNavigate();
  const cfg  = statusConfig[batch.status] || statusConfig.locked;
  const isGrand = batch.batch_number === 100;

  return (
    <div style={{
      background: '#fff',
      border: `2px solid ${batch.status === 'unlocked' ? 'var(--blue)' : batch.status === 'completed' ? '#28A745' : 'var(--border)'}`,
      borderRadius: 14,
      padding: '18px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      transition: 'box-shadow 0.2s, transform 0.2s',
      opacity: batch.status === 'locked' ? 0.65 : 1,
      cursor: batch.status === 'locked' ? 'default' : 'pointer',
      boxShadow: batch.status === 'unlocked' ? '0 4px 18px rgba(26,60,143,0.13)' : '0 1px 4px rgba(0,0,0,0.05)',
      position: 'relative',
      overflow: 'hidden',
    }}
    onClick={() => batch.status !== 'locked' && onStart(batch)}
    >
      {isGrand && (
        <div style={{ position:'absolute', top:0, right:0, background:'linear-gradient(135deg,#FFD700,#FFA500)', color:'#fff', fontSize:11, fontWeight:700, padding:'3px 10px', borderBottomLeftRadius:10 }}>
          GRAND FINAL
        </div>
      )}
      {/* Batch Number Circle */}
      <div style={{
        width: 50, height: 50, borderRadius: '50%', flexShrink: 0,
        background: batch.status === 'unlocked' ? 'linear-gradient(135deg,var(--blue),var(--blue-mid))' :
                    batch.status === 'completed' ? 'linear-gradient(135deg,#28A745,#20c997)' :
                    'var(--border)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: batch.status === 'locked' ? 'var(--muted)' : '#fff',
        fontFamily: 'var(--font-heading)', fontWeight: 900,
        fontSize: batch.batch_number > 99 ? 14 : batch.batch_number > 9 ? 16 : 20,
      }}>
        {batch.status === 'locked' ? '🔒' : batch.batch_number}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily:'var(--font-heading)', fontWeight:700, fontSize:15, marginBottom:3, color:'var(--text)' }}>
          Batch {batch.batch_number} {isGrand ? '🏆' : ''}
        </div>
        <div style={{ fontSize:12, color:'var(--muted)', display:'flex', gap:12, flexWrap:'wrap' }}>
          <span>📝 {batch.total_questions} Questions</span>
          <span>⏱ {batch.duration_minutes} mins</span>
          {batch.best_percentage !== null && <span>🎯 Best: <strong style={{ color:'var(--blue)' }}>{batch.best_percentage}%</strong></span>}
          {batch.attempts > 0 && <span>🔁 {batch.attempts}x</span>}
        </div>
      </div>

      {/* Status badge + action */}
      <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6 }}>
        <span style={{
          background: cfg.bg, color: cfg.color,
          fontSize: 11, fontWeight: 700, padding:'3px 10px',
          borderRadius: 20, whiteSpace:'nowrap'
        }}>
          {cfg.icon} {cfg.label}
        </span>
        {batch.status === 'unlocked' && (
          <button className="btn btn-primary" style={{ fontSize:12, padding:'5px 14px', borderRadius:8 }}
            onClick={e => { e.stopPropagation(); onStart(batch); }}>
            Start →
          </button>
        )}
        {batch.status === 'completed' && (
          <button className="btn btn-outline" style={{ fontSize:12, padding:'5px 14px', borderRadius:8 }}
            onClick={e => { e.stopPropagation(); onStart(batch); }}>
            Retry
          </button>
        )}
      </div>
    </div>
  );
}

export default function Tests() {
  const nav = useNavigate();
  const [batches,  setBatches]  = useState([]);
  const [summary,  setSummary]  = useState({});
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState('all');
  const [search,   setSearch]   = useState('');

  useEffect(() => {
    batchAPI.getAll()
      .then(r => { setBatches(r.data.batches || []); setSummary(r.data.summary || {}); })
      .catch(() => toast.error('Failed to load batches'))
      .finally(() => setLoading(false));
  }, []);

  const handleStart = (batch) => {
    nav(`/dashboard/batch/${batch.id}/test`);
  };

  const filtered = batches.filter(b => {
    const matchFilter =
      filter === 'all'       ? true :
      filter === 'available' ? b.status === 'unlocked' :
      filter === 'completed' ? b.status === 'completed' :
      filter === 'locked'    ? b.status === 'locked' : true;
    const matchSearch = search === '' || `Batch ${b.batch_number}`.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  if (loading) return (
    <DashboardLayout title="My Batches" subtitle="Loading your batch progress...">
      <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:400 }}>
        <Spinner size={40} color="blue" />
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout title="My Batches 📚" subtitle={`Complete all 100 batches to master TNPSC`}>
      {/* Progress Bar */}
      <div style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:16, padding:20, marginBottom:20 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
          <div style={{ fontFamily:'var(--font-heading)', fontWeight:700, fontSize:16 }}>
            Overall Progress
          </div>
          <div style={{ fontFamily:'var(--font-heading)', fontWeight:900, fontSize:22, color:'var(--blue)' }}>
            {summary.completed || 0} / {summary.total || 100}
          </div>
        </div>
        <div style={{ background:'var(--border)', borderRadius:99, height:12, overflow:'hidden' }}>
          <div style={{
            height:'100%', borderRadius:99,
            background:'linear-gradient(90deg,var(--blue),var(--blue-mid))',
            width:`${((summary.completed||0)/(summary.total||100))*100}%`,
            transition:'width 0.6s ease',
          }} />
        </div>
        <div style={{ display:'flex', gap:20, marginTop:14, flexWrap:'wrap' }}>
          {[
            { label:'Completed', count: summary.completed||0, color:'#28A745', icon:'✅' },
            { label:'Available', count: summary.available||0, color:'var(--blue)', icon:'🔓' },
            { label:'Locked',    count: summary.locked||0,    color:'var(--muted)', icon:'🔒' },
          ].map(s => (
            <div key={s.label} style={{ display:'flex', alignItems:'center', gap:7 }}>
              <span style={{ fontSize:16 }}>{s.icon}</span>
              <span style={{ fontWeight:700, color:s.color, fontFamily:'var(--font-heading)' }}>{s.count}</span>
              <span style={{ color:'var(--muted)', fontSize:13 }}>{s.label}</span>
            </div>
          ))}
          {summary.nextBatch && (
            <div style={{ marginLeft:'auto', fontSize:13, color:'var(--blue)', fontWeight:600 }}>
              Next: Batch {summary.nextBatch.batch_number} ready! →
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <input
          placeholder="Search batch..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding:'8px 14px', borderRadius:10, border:'1px solid var(--border)', fontSize:13, width:160, outline:'none' }}
        />
        {['all','available','completed','locked'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            style={{
              padding:'7px 16px', borderRadius:99, fontSize:12, fontWeight:600, border:'none', cursor:'pointer',
              background: filter===f ? 'var(--blue)' : 'var(--border)',
              color: filter===f ? '#fff' : 'var(--text)',
            }}>
            {f === 'all' ? `All (${batches.length})` :
             f === 'available' ? `🔓 Available (${summary.available||0})` :
             f === 'completed' ? `✅ Done (${summary.completed||0})` :
             `🔒 Locked (${summary.locked||0})`}
          </button>
        ))}
      </div>

      {/* Batch Grid */}
      {filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 20px', color:'var(--muted)' }}>
          <div style={{ fontSize:60, marginBottom:12 }}>📭</div>
          <p style={{ fontSize:16 }}>No batches match your filter.</p>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {filtered.map(batch => (
            <BatchCard key={batch.id} batch={batch} onStart={handleStart} />
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
